import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../utils/api';
import { Project } from '../types/api';

const PROJECTS_PER_PAGE = 10;

interface UseProjectsReturn {
  projects: Project[];
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  currentPage: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  loadMore: () => void;
  lastProjectRef: (node: HTMLDivElement | null) => void;
}

export const useProjects = (): UseProjectsReturn => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const loadingRef = useRef(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentSearchRef = useRef('');
  const initialLoadDone = useRef(false);

  const loadProjects = useCallback(async (page: number, search: string, isNewSearch = false) => {
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      setError(null);
      
      // Determine the type of loading
      const isFirstLoad = page === 1 && (isNewSearch || !initialLoadDone.current);
      if (isFirstLoad) {
        setIsInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await apiService.getProjects({
        offset: (page - 1) * PROJECTS_PER_PAGE,
        limit: PROJECTS_PER_PAGE,
        search: search || ''
      });

      // Check if search query changed during loading
      if (currentSearchRef.current !== search) {
        return;
      }
      
      // Use the correct response structure
      const projectsData: Project[] = response.data || [];
      const total = response.total || projectsData.length;
      
      if (isNewSearch || page === 1) {
        setProjects(projectsData);
        setCurrentPage(1);
      } else {
        setProjects(prev => {
          const existingIds = new Set(prev.map(p => p.project_id));
          const newProjects = projectsData.filter(project => !existingIds.has(project.project_id));
          return [...prev, ...newProjects];
        });
      }
      
      setTotalCount(total);
      
      // Fix hasMore logic
      const loadedCount = isNewSearch || page === 1 ? projectsData.length : page * PROJECTS_PER_PAGE;
      setHasMore(projectsData.length === PROJECTS_PER_PAGE && loadedCount < total);
      
      // Mark initial load as complete
      if (isFirstLoad) {
        initialLoadDone.current = true;
      }
      
    } catch (err) {
      // console.error('Error loading projects:', err);
      setError('Failed to load projects');
      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, []); // Remove all dependencies to avoid infinite rerenders

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    currentSearchRef.current = query;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setProjects([]);
      setCurrentPage(1);
      setHasMore(true);
      initialLoadDone.current = false; // Reset flag on new search
      loadProjects(1, query, true);
    }, 500);
  }, [loadProjects]);

  const loadMore = useCallback(() => {
    if (!hasMore || isInitialLoading || isLoadingMore || loadingRef.current) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadProjects(nextPage, searchQuery);
  }, [hasMore, isInitialLoading, isLoadingMore, currentPage, searchQuery, loadProjects]);

  const lastProjectRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || isInitialLoading || isLoadingMore || !hasMore || loadingRef.current) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current && !isInitialLoading && !isLoadingMore) {
        loadMore();
      }
    }, {
      rootMargin: '100px'
    }); 
    
    observer.current.observe(node);
  }, [hasMore, isInitialLoading, isLoadingMore, loadMore]);

      // Initial load - call only once
  useEffect(() => {
    if (!initialLoadDone.current) {
      loadProjects(1, '');
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loadProjects]); // Include loadProjects in dependencies

  return {
    projects,
    isInitialLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    currentPage,
    searchQuery,
    setSearchQuery: handleSearchChange,
    loadMore,
    lastProjectRef
  };
}; 